import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Table, TableHead, TableBody,
  TableRow, TableCell, CircularProgress, Alert, Chip, Snackbar,
  Tooltip, Divider,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';

const TERMINAL_STATUSES = ['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'ABORTED'];
const POLL_INTERVAL = 10000; // 10 seconds
const MAX_CONSECUTIVE_ERRORS = 3;

/**
 * Extract a date string from a report filename matching the pattern _{YYYY-MM-DD}.xlsx
 */
function extractDateFromFilename(fileName) {
  const match = fileName && fileName.match(/_(\d{4}-\d{2}-\d{2})\.xlsx$/);
  return match ? match[1] : '—';
}

/**
 * Format bytes into a human-readable string (e.g. "12.5 KB")
 */
function formatFileSize(bytes) {
  if (bytes == null || isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

export default function AccessReportSection({ accountId, authenticatedFetch, API_ENDPOINT, canEdit }) {
  const [reports, setReports] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [executionArn, setExecutionArn] = useState(null);
  const [executionStatus, setExecutionStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const pollRef = useRef(null);
  const errorCountRef = useRef(0);

  // ── Fetch report history ──────────────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    if (!accountId) return;
    setLoadingHistory(true);
    setError(null);
    try {
      const res = await authenticatedFetch(`${API_ENDPOINT}/accounts/${accountId}/access-report/history`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setReports(json.reports || []);
    } catch (e) {
      setError('Failed to load report history');
    } finally {
      setLoadingHistory(false);
    }
  }, [accountId, authenticatedFetch, API_ENDPOINT]);

  // On mount / accountId change → load history
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // ── Generate report ───────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setExecutionStatus(null);
    try {
      const res = await authenticatedFetch(`${API_ENDPOINT}/accounts/${accountId}/access-report/generate`, {
        method: 'POST',
      });
      if (res.status === 403) {
        setError('Admin access is required to generate reports');
        setGenerating(false);
        return;
      }
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      if (json.executionArn) {
        setExecutionArn(json.executionArn);
        setExecutionStatus('RUNNING');
        errorCountRef.current = 0;
        setSnackbar({ open: true, message: 'Report generation started', severity: 'info' });
      } else {
        // No executionArn — generation started but we can't poll status
        setGenerating(false);
        setSnackbar({ open: true, message: json.message || 'Report generation started', severity: 'success' });
        // Refresh history after a delay to pick up the new report
        setTimeout(fetchHistory, 15000);
      }
    } catch (e) {
      setError(e.message || 'Failed to start report generation');
      setGenerating(false);
    }
  };

  // ── Status polling ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!executionArn || !accountId) return;

    const pollStatus = async () => {
      try {
        const res = await authenticatedFetch(
          `${API_ENDPOINT}/accounts/${accountId}/access-report/status?executionArn=${encodeURIComponent(executionArn)}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        errorCountRef.current = 0;
        setExecutionStatus(json.status);

        if (TERMINAL_STATUSES.includes(json.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          setExecutionArn(null);

          if (json.status === 'SUCCEEDED') {
            setSnackbar({ open: true, message: 'Report generated successfully', severity: 'success' });
            fetchHistory();
          } else {
            setSnackbar({ open: true, message: `Report generation ${json.status.toLowerCase()}`, severity: 'error' });
          }
        }
      } catch {
        errorCountRef.current += 1;
        if (errorCountRef.current >= MAX_CONSECUTIVE_ERRORS) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          setExecutionArn(null);
          setExecutionStatus(null);
          setSnackbar({ open: true, message: 'Lost connection while checking report status', severity: 'error' });
        }
      }
    };

    // Poll immediately, then every 10s
    pollStatus();
    pollRef.current = setInterval(pollStatus, POLL_INTERVAL);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [executionArn, accountId, authenticatedFetch, API_ENDPOINT, fetchHistory]);

  // ── Download ──────────────────────────────────────────────────────────────
  const handleDownload = async (s3Key) => {
    try {
      const res = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/${accountId}/access-report/download?key=${encodeURIComponent(s3Key)}`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.url) {
        window.open(json.url, '_blank');
      }
    } catch {
      setSnackbar({ open: true, message: 'Failed to generate download link', severity: 'error' });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Section header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AssessmentIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
            Report History
          </Typography>
          {reports.length > 0 && (
            <Chip label={reports.length} size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {generating && executionStatus && (
            <Chip
              label={executionStatus}
              size="small"
              color={executionStatus === 'RUNNING' ? 'info' : 'default'}
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
          )}
          <Tooltip title="Refresh history">
            <IconButton size="small" onClick={fetchHistory} disabled={loadingHistory} sx={{ p: 0.5 }}>
              <RefreshIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          {canEdit && (
            <Button
              variant="outlined"
              size="small"
              startIcon={generating ? <CircularProgress size={13} /> : <SendIcon sx={{ fontSize: 14 }} />}
              onClick={handleGenerate}
              disabled={generating}
              sx={{ fontSize: '0.75rem', textTransform: 'none', minWidth: 0, py: 0.25, px: 1 }}
            >
              {generating ? 'Generating...' : 'Generate Report'}
            </Button>
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 0.75 }} />

      {/* Error display */}
      {error && (
        <Alert
          severity="error"
          sx={{ fontSize: '0.78rem', mb: 1 }}
          action={
            <Button color="inherit" size="small" onClick={error.includes('Admin') ? undefined : handleGenerate} sx={{ fontSize: '0.7rem' }}>
              {error.includes('Admin') ? '' : 'Retry'}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Loading state */}
      {loadingHistory && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          <CircularProgress size={14} />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
            Loading report history...
          </Typography>
        </Box>
      )}

      {/* Empty state */}
      {!loadingHistory && reports.length === 0 && !error && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, color: 'text.disabled' }}>
          <AssessmentIcon sx={{ fontSize: 28, mb: 0.5 }} />
          <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
            No reports available
          </Typography>
        </Box>
      )}

      {/* Report history table */}
      {!loadingHistory && reports.length > 0 && (
        <Table size="small" sx={{ '& td, & th': { py: 0.5, px: 1, fontSize: '0.78rem', borderBottom: '1px solid', borderColor: 'divider' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>File</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Size</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="center">Download</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.slice(0, 3).map((report) => (
              <TableRow key={report.key} hover>
                <TableCell>{extractDateFromFilename(report.fileName)}</TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={report.fileName}>
                    <span>{report.fileName}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">{formatFileSize(report.size)}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Download report">
                    <IconButton size="small" onClick={() => handleDownload(report.key)} sx={{ p: 0.25 }}>
                      <DownloadIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ fontSize: '0.82rem' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
